import React, { useState, useEffect } from 'react';
import { Mail, X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface EmailVerificationBannerProps {
  token: string;
}

export const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({ token }) => {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [lastSentAt, setLastSentAt] = useState<Date | null>(null);

  useEffect(() => {
    // Check email verification status
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/email/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setIsVerified(data.email_verified);
          setIsVisible(!data.email_verified);
          
          if (data.verification_sent_at) {
            setLastSentAt(new Date(data.verification_sent_at));
          }
        }
      } catch (error) {
        console.error('Failed to check email status:', error);
      }
    };

    checkStatus();
  }, [token]);

  const handleResendEmail = async () => {
    // Check if user needs to wait (30 seconds cooldown)
    if (lastSentAt) {
      const timeSinceLastSent = Date.now() - lastSentAt.getTime();
      const cooldown = 30000; // 30 seconds
      
      if (timeSinceLastSent < cooldown) {
        const remainingSeconds = Math.ceil((cooldown - timeSinceLastSent) / 1000);
        toast.error(t('email.banner.cooldown', { seconds: remainingSeconds }));
        return;
      }
    }

    setIsSending(true);

    try {
      const response = await fetch(`${API_URL}/email/send-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('email.banner.sendFail'));
      }

      setLastSentAt(new Date());
      toast.success(t('email.banner.sentSuccess'));
    } catch (error: any) {
      toast.error(error.message || t('email.banner.sendFail'));
    } finally {
      setIsSending(false);
    }
  };

  if (!isVisible || isVerified) {
    return null;
  }

  return (
    <div className="mb-6 relative">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg blur opacity-30"></div>
      <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="mt-0.5">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-1">
                {t('email.banner.title')}
              </h3>
              <p className="text-blue-100 text-sm mb-3">
                {t('email.banner.description')}
              </p>
              <button
                onClick={handleResendEmail}
                disabled={isSending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm border border-white/20"
              >
                {isSending ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    {t('email.banner.sending')}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    {t('email.banner.resendButton')}
                  </>
                )}
              </button>
            </div>
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="text-white/80 hover:text-white transition-colors p-1"
            aria-label={t('actions.dismiss')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

